import React, { useCallback, useEffect, useState } from "react";
import { Tag, Collapse } from "antd";
import {
  FileTextOutlined,
  CaretDownOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import Modal from "../../../components/generic/Modal";
import { StyledQuestionsContainer } from "./styles";
import { useDispatch } from "react-redux";
import { editTopic } from "../../../store/topics/thunks";

const { Panel } = Collapse;

const QuestionsListModal = ({ handleCancel, selectedTopic }) => {
  const [questions, setQuestions] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedTopic?.questions) setQuestions(selectedTopic?.questions);
  }, [selectedTopic?.questions]);

  const handleDragStart = useCallback((e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback(
    (e, index) => {
      e.preventDefault();
      setDragOverIndex(index);

      if (draggedIndex === null || draggedIndex === index) return;

      const newQuestions = [...questions];
      const draggedItem = newQuestions[draggedIndex];

      newQuestions.splice(draggedIndex, 1);
      newQuestions.splice(index, 0, draggedItem);

      setQuestions(newQuestions);
      setDraggedIndex(index);
    },
    [draggedIndex, questions, dragOverIndex]
  );

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      setSubmitting(true);

      await dispatch(
        editTopic({
          tenant_id: selectedTopic?.tenant_id,
          topic_id: selectedTopic.id,
          branch_ids: selectedTopic?.branch_ids,
          questions: questions?.map((q, i) => ({ ...q, order: i + 1 })),
        })
      );

      handleCancel();
    } finally {
      setSubmitting(false);
    }
  }, [selectedTopic, questions]);

  return (
    <Modal
      isProcessing={submitting}
      title={`Arrange Questions (${questions.length})`}
      onCancel={handleCancel}
      onOk={handleSubmit}
      width={600}
      open
    >
      <StyledQuestionsContainer>
        <div className="drag-instruction">
          <HolderOutlined />
          <span>&nbsp; Drag and drop to reorder questions</span>
        </div>
        <Collapse className="collapse-wrapper">
          {questions.map((item, index) => (
            <Panel
              key={item.id}
              className={`
              ${draggedIndex === index ? "dragging" : ""}
              ${dragOverIndex === index ? "drag-over" : ""}
            `}
              header={
                <div
                  className="flex align-center w-100 panel-header"
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDragEnd={handleDragEnd}
                >
                  <HolderOutlined className="drag-handle" />
                  {item.type === "dropdown" ? (
                    <CaretDownOutlined className="flex-centre question-icon dropdown-type" />
                  ) : (
                    <FileTextOutlined className="flex-centre question-icon string-type" />
                  )}
                  <div className="question-info">
                    <span className="question-number ellipsis-text">
                      {item?.question}
                    </span>
                    <Tag
                      color={item.type === "dropdown" ? "blue" : "green"}
                      className="question-type-tag"
                    >
                      {item.type}
                    </Tag>
                  </div>
                </div>
              }
            >
              <div>
                <p className="question-text">{item.question}</p>
                {item.options && (
                  <div className="options-section">
                    <span className="options-label">Available Options:</span>
                    <div className="options-list">
                      {item.options.map((option, idx) => (
                        <span key={idx} className="option-tag">
                          {option}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Panel>
          ))}
        </Collapse>
      </StyledQuestionsContainer>
    </Modal>
  );
};

export default QuestionsListModal;
